import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Truck, Package, Zap, Info, AlertTriangle, HelpCircle, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const DEBOUNCE_MS = 400;

const MerchantSuccessTooltip = ({ onClose }) => (
  <div className="absolute right-0 top-8 z-50 w-72 bg-white rounded-xl shadow-xl border border-slate-200 p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="merchant-success-tooltip">
    <div className="flex items-start justify-between">
      <h4 className="text-sm font-semibold text-slate-800">Why the Shipping Pool?</h4>
      <button onClick={onClose} className="text-slate-400 hover:text-slate-600 -mt-0.5"><X className="h-3.5 w-3.5" /></button>
    </div>
    <div className="space-y-2.5 text-xs text-slate-600 leading-relaxed">
      <p>
        <strong className="text-red-600">70%</strong> of online carts are abandoned when customers see unexpected shipping charges at checkout.
      </p>
      <p>
        The <strong>5% shipping contribution</strong> funds a transparent subsidy pool so customers see lower — or free — delivery costs at checkout.
      </p>
      <p>
        Aniyor also adds a <strong className="text-indigo-600">3% platform subsidy</strong> from its reserve to further reduce shipping costs for your customers.
      </p>
      <div className="bg-emerald-50 border border-emerald-100 rounded-md p-2 text-emerald-700 text-[11px]">
        Result: Higher conversions, fewer abandoned carts, and a better buying experience for your customers.
      </div>
    </div>
  </div>
);

const ShippingPricingCard = ({ formData, handleChange, listingType }) => {
  const { apiCall } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const timerRef = useRef(null);

  const price = parseFloat(formData?.price) || 0;
  const mrp = parseFloat(formData?.mrpPrice) || 0;
  const absorbShipping = !!formData?.absorbShipping;
  const requiresShipping = formData?.requiresShipping !== false;
  // All rentals are location-based or hyperlocal — no standard shipping pool applies
  const isVenueRental = listingType === 'rental';
  const isService = ['service', 'event', 'volunteering'].includes(listingType) || isVenueRental;
  const isDigital = formData?.productType === 'digital';
  const showShipping = !isService && !isDigital && requiresShipping;

  const fetchPreview = useCallback(async (basePrice, shipping, mrpVal, absorb) => {
    if (basePrice <= 0) {
      setPreview(null);
      return;
    }
    setLoading(true);
    try {
      const body = {
        base_price: basePrice,
        speed: 'surface',
        requires_shipping: shipping,
      };
      if (mrpVal > 0) {
        body.mrp_price = mrpVal;
        body.absorb_shipping = absorb;
      }
      const data = await apiCall('/api/shipping-settings/pricing-preview', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      fetchPreview(price, showShipping, mrp, absorbShipping);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [price, showShipping, mrp, absorbShipping, fetchPreview]);

  if (price <= 0 && !preview) return null;

  const contribution = preview?.shipping_contribution ?? 0;
  const listingPrice = preview?.aniyor_listing_price ?? price;
  const wasCapped = preview?.was_capped ?? false;
  const absorbRequired = preview?.absorb_required ?? false;
  const absorbed = preview?.absorbed ?? false;

  return (
    <Card className="border-slate-200" data-testid="shipping-pricing-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800">
          <Package className="h-4 w-4" />
          Pricing Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Vendor Base Price */}
        <div className="flex items-center justify-between" data-testid="vendor-base-price">
          <span className="text-sm text-slate-600">Vendor Base Price</span>
          <span className="text-sm font-medium text-slate-900">
            {price > 0 ? `₹${price.toLocaleString('en-IN')}` : '—'}
          </span>
        </div>

        {/* MRP line (if set) */}
        {mrp > 0 && (
          <div className="flex items-center justify-between" data-testid="mrp-display">
            <span className="text-sm text-slate-600">MRP Ceiling</span>
            <span className="text-sm font-medium text-slate-500">
              ₹{mrp.toLocaleString('en-IN')}
            </span>
          </div>
        )}

        {/* 5% Shipping Contribution */}
        <div
          className={`flex items-center justify-between ${!showShipping ? 'opacity-40' : ''}`}
          data-testid="shipping-contribution-row"
        >
          <div className="flex items-center gap-1.5">
            <Truck className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-sm text-slate-600">Shipping Pool (5%)</span>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTooltip(v => !v)}
                className="text-slate-400 hover:text-indigo-500 transition-colors"
                data-testid="merchant-success-info-btn"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
              {showTooltip && <MerchantSuccessTooltip onClose={() => setShowTooltip(false)} />}
            </div>
            {wasCapped && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-300 text-amber-600">
                Capped
              </Badge>
            )}
            {absorbed && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 border-blue-300 text-blue-600">
                Absorbed
              </Badge>
            )}
          </div>
          <span className="text-sm font-medium text-slate-900">
            {showShipping && contribution > 0
              ? `+ ₹${contribution.toLocaleString('en-IN')}`
              : '₹0'}
          </span>
        </div>

        {!showShipping && (
          <p className="text-xs text-slate-400 flex items-center gap-1" data-testid="no-shipping-note">
            <Info className="h-3 w-3" />
            {isService ? 'Services don\'t require shipping' : 'Digital product — no shipping'}
          </p>
        )}

        {/* MRP Warning & Absorb Toggle */}
        {absorbRequired && !absorbed && showShipping && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 space-y-2" data-testid="mrp-warning">
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Listing price (₹{(price + (preview?.original_bump ?? 0)).toLocaleString('en-IN')}) exceeds MRP (₹{mrp.toLocaleString('en-IN')}).
              You must absorb the shipping contribution.
            </p>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="absorb-shipping-toggle">
              <input
                type="checkbox"
                checked={absorbShipping}
                onChange={(e) => handleChange && handleChange('absorbShipping', e.target.checked)}
                className="rounded border-amber-400 text-amber-600 focus:ring-amber-500 h-3.5 w-3.5"
              />
              <span className="text-xs font-medium text-amber-800">
                I will absorb the shipping contribution to stay within MRP
              </span>
            </label>
          </div>
        )}

        {absorbed && (
          <p className="text-xs text-blue-500 flex items-center gap-1" data-testid="absorbed-note">
            <Info className="h-3 w-3" />
            Shipping contribution absorbed. Listing price capped at MRP.
          </p>
        )}

        {/* Divider */}
        <div className="border-t border-dashed border-slate-200 pt-2">
          <div className="flex items-center justify-between" data-testid="aniyor-listing-price">
            <div className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-sm font-semibold text-slate-800">Listing Price</span>
            </div>
            <span className="text-base font-bold text-indigo-600">
              {loading ? '...' : listingPrice > 0 ? `₹${listingPrice.toLocaleString('en-IN')}` : '—'}
            </span>
          </div>
        </div>

        {/* Info */}
        {showShipping && price > 0 && !absorbRequired && (
          <p className="text-[11px] text-slate-400 leading-snug mt-1">
            5% of your base price goes into a shipping subsidy pool.
            {wasCapped && ` Capped at ₹${preview?.max_zone_e_cap?.toLocaleString('en-IN')}.`}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ShippingPricingCard;
